async function getCachedContents(url) {
    if (!('serviceWorker' in window.navigator && window.navigator.serviceWorker.controller)) {
        return null;
    }

    const response = await caches.match(url, { ignoreVary: true });

    if (!response) {
        return null;
    }

    return await response.text();
}

class ViewResource {
    elements = new Map();
    url;

    check(source) {
        return !source.hasAttribute('bolt-pin') && source[this.url] !== '' && source[this.url].startsWith(window.location.origin + '/views/');
    }

    queue(source) {
        source.isQueued = true;

        return source;
    }

    async create(source) {
        if (!this.has(source[this.url])) {
            let contents = await getCachedContents(source[this.url]);

            if (!contents) {
                const response = await axios.get(source[this.url], {
                    headers: {
                        Accept: '*/*',
                        'X-Requested-With': null,
                    },
                });

                contents = response.data;
            }

            this.add(source[this.url], contents);
        }

        return this.template(source);
    }

    has(name) {
        return this.elements.has(name);
    }

    add(name, element) {
        this.elements.set(name, element);
    }

    get(name) {
        return this.elements.get(name);
    }

    remove(name) {
        this.elements.delete(name);
    }
}

class ViewStyle extends ViewResource {
    url = 'href';

    template(source) {
        const style = document.createElement('style');
        style.innerHTML = this.get(source.href);

        return style;
    }
}

class ViewScript extends ViewResource {
    url = 'src';

    template(source) {
        const script = document.createElement('script');
        script.innerHTML = `Bolt.viewScript.run('${source.src}')`;
        script.async = false;

        return script;
    }

    add(name, callback) {
        if (typeof callback === 'string') {
            callback = callback
                .replace(/^\(\(\) ?=> ?{/, '')
                .replace(/}\)\(\);$/, '')
                .trim();

            try {
                callback = new Function(callback);
            } catch (error) {
                console.error(error);
            }
        }

        this.elements.set(name, callback);
    }

    run(name) {
        this.elements.get(name)();
    }
}

export default class {
    static #enabled = false;
    static #showTimingLogs = false;
    static #targetSelectors = [];
    static #currentUrl = '';
    static #skipPopStateHandling = () => false;
    static #isPopFromHistory = false;
    static #counter = 0;
    static #timers = {};
    static #eventListeners = new Map();
    static #scrollPositions = {};
    static #currentScrollPosition = 0;
    static #preserveCurrentScroll = false;
    static #urlHash = null;
    static #responsesCache = new Map();
    static #abortController = null;

    static viewStyle = new ViewStyle();
    static viewScript = new ViewScript();

    static get enabled() {
        return this.#enabled;
    }

    static get showTimingLogs() {
        return this.#showTimingLogs;
    }

    static set showTimingLogs(value) {
        this.#showTimingLogs = value;
        localStorage.setItem('bolt_show_timing_logs', JSON.stringify(value));
    }

    static start() {
        const startTime = performance.now();

        // Get config
        this.#enabled = JSON.parse(this.getMetadata(document, 'bolt-enabled', false));
        this.#showTimingLogs = JSON.parse(localStorage.getItem('bolt_show_timing_logs')) ?? false;

        // Configure CSRF token for axios
        this.refreshAxiosCsrfToken(document);

        const getLinkElement = (element) => (element instanceof HTMLAnchorElement ? element : element.closest('a'));

        const isInvalidLink = (element) =>
            !element ||
            !element.href ||
            element.download ||
            (element.outerHTML.includes('click') && element.outerHTML.includes('prevent')) ||
            ['_blank', '_parent', '_top'].includes(element.target) ||
            this.isElementDisabled(element);

        const isInvalidUrl = (url) =>
            url.href.endsWith('#') ||
            url.origin !== window.location.origin ||
            (url.hash && url.href === window.location.href) ||
            (url.hash && url.pathname === window.location.pathname);

        // Preload resources from link header on mouse events
        const preloaded = new Map();
        const preloading = new Map();

        const removePreloadingRequest = (url) => {
            window.clearTimeout(preloading.get(url.href));
            preloading.delete(url.href);
        };

        document.querySelectorAll('[bolt-preload-resources-on-hover]').forEach((element) => {
            element.addEventListener('mouseover', (event) => {
                if (!this.#enabled) return;

                const element = getLinkElement(event.target);

                if (isInvalidLink(element)) return;

                const url = new URL(element.href);

                if (isInvalidUrl(url) || url.pathname === window.location.pathname || preloaded.has(url.href) || preloading.has(url.href)) return;

                preloading.set(
                    url.href,
                    window.setTimeout(async () => {
                        preloaded.set(url.href, true);

                        const response = await this.request({
                            method: 'head',
                            url,
                            config: this.axiosConfig(),
                            timerConfig: {
                                name: 'preloading resources from link header',
                                template: 'preloading resources from link header in %duration ms',
                            },
                            abortable: false,
                        });

                        if (response && response.status === 200) {
                            (response.headers?.link ?? '')
                                .split(',')
                                .filter((entry) => entry !== '')
                                .forEach((entry) => this.preloadResourceEntry(entry));
                        }
                    }, 300)
                );
            });

            element.addEventListener('mouseout', (event) => {
                if (!this.#enabled) return;

                const element = getLinkElement(event.target);

                if (isInvalidLink(element)) return;

                removePreloadingRequest(new URL(element.href));
            });
        });

        // Add click event listener
        const isInvalidClickEvent = (event) => event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;

        const visitUrl = async (url) => {
            removePreloadingRequest(url);
            preloaded.set(url.href, true);

            return await this.go.bind(this)(url, false);
        };

        let visitUrlPromises = new Map();
        let eventType;

        const preclickListener = (event) => {
            if (!this.#enabled || isInvalidClickEvent(event)) return;

            const element = getLinkElement(event.target);

            if (isInvalidLink(element)) return;

            const url = new URL(element.href);

            if (isInvalidUrl(url) || visitUrlPromises.has(url.href)) return;

            eventType = event.type;
            this.timerStart(eventType);
            visitUrlPromises.set(url.href, visitUrl(url));
            window.setTimeout(() => visitUrlPromises.delete(url.href), 5000);
        };

        ['touchstart', 'mousedown'].forEach((type) => document.addEventListener(type, preclickListener, { passive: true }));

        document.addEventListener('click', (event) => {
            if (!this.#enabled || isInvalidClickEvent(event)) return;

            const element = getLinkElement(event.target);

            if (isInvalidLink(element)) return;

            this.timerLog(eventType, `${eventType} in %duration ms`);

            const url = new URL(element.href);

            if (url.origin !== window.location.origin || (url.hash && url.href === window.location.href)) return;

            event.preventDefault();

            if (url.href.endsWith('#')) return;

            if (url.hash && url.pathname === window.location.pathname) {
                this.#currentUrl = window.location.href;
                this.#scrollPositions[this.#currentUrl] = window.scrollY;

                window.history.scrollRestoration = 'manual';

                window.history.replaceState(
                    {
                        name: 'bolt',
                        url: this.#currentUrl,
                    },
                    '',
                    this.#currentUrl
                );

                window.history.pushState(
                    {
                        name: 'bolt',
                        url: url.href,
                    },
                    '',
                    url.href
                );

                this.#currentUrl = url.href;

                document.getElementById(url.hash.slice(1))?.scrollIntoView();

                return;
            }

            if (element.closest('[bolt-preserve-scroll]')) {
                this.#preserveCurrentScroll = true;
            }

            this.startProgressBar();

            if (!visitUrlPromises.has(url.href)) {
                visitUrlPromises.set(url.href, visitUrl(url));
            }

            visitUrlPromises.get(url.href).then(async (response) => {
                response && (await this.renderView(response.request.responseURL, response.data, !element.hasAttribute('bolt-skip-view-transition')));
                this.stopProgressBar();
                visitUrlPromises.delete(url.href);
            });
        });

        // Set history
        this.#currentUrl = window.location.href;

        window.history.scrollRestoration = 'manual';

        window.history.replaceState(
            {
                name: 'bolt',
                url: this.#currentUrl,
            },
            '',
            this.#currentUrl
        );

        window.addEventListener('popstate', (event) => {
            if (!this.#enabled || this.#skipPopStateHandling(event)) return;

            const targetUrl = window.location.href;

            if (targetUrl.split('#')[0] === this.#currentUrl.split('#')[0]) {
                this.#scrollPositions[this.#currentUrl] = window.scrollY;
                this.#currentUrl = targetUrl;

                window.scrollTo({ top: this.#scrollPositions[targetUrl] ?? 0 });

                window.history.replaceState(
                    {
                        name: 'bolt',
                        url: targetUrl,
                    },
                    '',
                    targetUrl
                );

                return;
            }

            this.#isPopFromHistory = true;
            this.#scrollPositions[this.#currentUrl] = window.scrollY;

            const response = this.#responsesCache.get(targetUrl);

            if (response && Date.now() - response.timestamp <= 15e3) {
                (async () => await this.renderView(response.request.responseURL, response.data.cloneNode(true)))();
            } else {
                this.go(targetUrl);
            }
        });

        // Listen for document ready state
        document.addEventListener('DOMContentLoaded', () => {
            this.emitEvent('load');
            Alpine.start();

            for (const elements of Object.values(this.getElements())) {
                elements.forEach((element) => {
                    if (!element.hasAttribute('bolt-pin')) {
                        element.setAttribute('bolt-transient', '');
                    }
                });
            }

            const elements = document.querySelectorAll('[x-data], [x-init]');

            this.once('after-render', () => {
                elements.forEach((element) => {
                    if (!document.contains(element)) {
                        Alpine.destroyTree(element);
                    }
                });
            });
        });

        // Periodic monitoring
        window.setInterval(() => {
            const now = Date.now();

            this.#responsesCache.forEach((response, url) => {
                if (now - response.timestamp > 15e3) {
                    this.#responsesCache.delete(url);
                }
            });
        }, 150e3);

        if (!this.#enabled) return;

        console.info(`%cBolt started in ${(performance.now() - startTime).toFixed(2)} ms ⚡`, 'color: orange');
    }

    static refreshAxiosCsrfToken(targetDocument) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = this.getMetadata(targetDocument, 'csrf-token', '');
    }

    static preserveScroll(flag = true) {
        this.#preserveCurrentScroll = flag;

        return this;
    }

    static async go(url, render = true, transitionEnabled = true) {
        if (!(url instanceof URL)) {
            url = String(url);
            url = new URL((url.startsWith('/') ? window.location.origin : '') + url);
        }

        if (url.searchParams.has('_ajax')) {
            url.searchParams.delete('_ajax');
        }

        if (url.searchParams.get('page') === '1') {
            url.searchParams.delete('page');
        }

        if (!this.#enabled) {
            window.location = url;

            return;
        }

        if (url.hash) {
            this.#urlHash = url.href;
        }

        if (render) {
            this.startProgressBar();
        }

        const response = await this.request({
            method: 'get',
            url,
            config: this.axiosConfig(),
            timerConfig: {
                name: 'fetch page',
                template: 'fetch page in %duration ms',
            },
        });

        if (response && response.status === 200) {
            this.#responsesCache.set(
                url.href,
                Object.assign({}, response, {
                    data: response.data.cloneNode(true),
                    timestamp: Date.now(),
                })
            );

            (response.headers?.link ?? '')
                .split(',')
                .filter((entry) => entry !== '')
                .forEach((entry) => this.preloadResourceEntry(entry));
        }

        if (!render) {
            return response;
        }

        response && (await this.renderView(response.request.responseURL, response.data, transitionEnabled));
        this.stopProgressBar();
    }

    static async preloadResourceEntry(entry) {
        const parameters = entry.trim().split(';');
        let url = parameters.shift().slice(1, -1);
        url = url.startsWith('/') ? `${window.location.origin}${url}` : url;

        if (
            url === '' ||
            (url.includes('/views/') && entry.includes('as=style') && this.viewStyle.has(url)) ||
            (url.includes('/views/') && entry.includes('as=script') && this.viewScript.has(url)) ||
            document.querySelector(`link[href="${url}"], script[src="${url}"]`) ||
            (await getCachedContents(url)) !== null
        )
            return;

        const link = document.createElement('link');
        link.href = url;

        parameters.forEach((parameter) => {
            parameter = parameter.trim();
            const index = parameter.indexOf('=');
            link.setAttribute(parameter.slice(0, index), parameter.slice(index + 1));
        });

        link.onload = () => (link.dataset.loaded = 1);

        document.head.appendChild(link);
    }

    static async submitForm(form, submitter = null) {
        if (!(form instanceof HTMLFormElement)) {
            throw new Error('The argument must be an instance of HTMLFormElement');
        }

        if (!this.#enabled || this.isElementDisabled(form)) {
            if (!document.contains(form)) {
                document.body.appendChild(form);
            }

            form.submit();

            return;
        }

        if (form.closest('[bolt-preserve-scroll]')) {
            this.#preserveCurrentScroll = true;
        }

        let url,
            contentType,
            isUrlEncoded = false;

        url = form.getAttribute('action');
        url = new URL((url.startsWith('/') ? window.location.origin : '') + url);
        contentType = form.getAttribute('enctype') ?? 'application/x-www-form-urlencoded';

        const config = this.axiosConfig();
        config.headers['Content-Type'] = contentType;

        const formData = new FormData(form);
        const queryData = {};

        if (submitter instanceof HTMLElement && submitter.name !== '') {
            formData.append(submitter.name, submitter.value);
        }

        if (contentType === 'application/x-www-form-urlencoded') {
            isUrlEncoded = true;

            for (const [field, value] of formData) {
                // Follow PHP convention
                if (field.endsWith('[]')) {
                    if (Object.hasOwn(queryData, field)) {
                        queryData[field].push(value);
                    } else {
                        queryData[field] = [value];
                    }
                } else {
                    queryData[field] = value;
                }
            }
        }

        this.startProgressBar();

        const response = await this.request({
            method: 'post',
            url,
            data: isUrlEncoded ? queryData : formData,
            config,
            timerConfig: {
                name: 'submit form',
                template: 'submit form in %duration ms',
            },
        });

        response && (await this.renderView(response.request.responseURL, response.data));
        this.stopProgressBar();
    }

    static async request({
        method,
        url,
        data = {},
        config = this.axiosConfig(),
        timerConfig = { name: 'request', template: 'perform request in %duration ms' },
        abortable = true,
    }) {
        this.timerStart(timerConfig.name);

        if (abortable && this.#abortController) {
            this.#abortController.abort();
        }

        Object.assign(config, { method, url, data });

        if (abortable) {
            this.#abortController = new AbortController();
            config.signal = this.#abortController.signal;
        }

        let response;

        try {
            response = await axios(config);
        } catch (error) {
            if (axios.isCancel(error)) {
                return null;
            }

            console.error(error);

            if (error.response) {
                this.#enabled = JSON.parse(this.getMetadata(error.response.data, 'bolt-enabled', false));
                response = error.response;
            } else if (error.code === 'ERR_NETWORK' && error.config.method === 'post') {
                this.go(window.location.href);
            }
        }

        this.#abortController = null;
        this.timerLog(timerConfig.name, timerConfig.template);

        return response;
    }

    static axiosConfig() {
        return {
            responseType: 'document',
            headers: {
                Accept: 'text/html',
                'Content-Type': null,
                'X-Bolt': 1,
                'X-Requested-With': null,
            },
        };
    }

    static async renderView(targetUrl, newDocument, transitionEnabled = true) {
        this.timerStart('render view');

        // Stop Alpine mutation observer
        Alpine.deferMutations();

        // Remove ignored elements
        newDocument.querySelectorAll('[bolt-ignored]').forEach((element) => element.remove());

        // Get document's HTML string
        const documentHtml = document.documentElement.innerHTML;

        // Clone elements
        const { links, styles, templates, scripts } = this.getElements();
        const clonedElements = [];
        const deferredScripts = [];
        const idPinnedElements = {};

        const isPinned = (source) => documentHtml.includes(source.outerHTML) && source.hasAttribute('bolt-pin');

        // Get elements which are pinned with ID
        for (const elements of [links, styles, templates, scripts]) {
            elements.forEach((element) => {
                const id = element.getAttribute('bolt-pin');

                if (id) {
                    idPinnedElements[id] = element;
                }
            });
        }

        // <link>
        for (const source of newDocument.head.querySelectorAll('link[rel="stylesheet"]')) {
            if (isPinned(source)) continue;

            const cloned = this.viewStyle.check(source) ? this.viewStyle.queue(source) : this.cloneElement(source);

            clonedElements.push(cloned);
        }

        // <style>
        for (const source of newDocument.head.querySelectorAll('style')) {
            if (isPinned(source)) continue;

            clonedElements.push(this.cloneElement(source));
        }

        // <template>
        for (const source of [...newDocument.body.querySelectorAll('template[id]')].filter((element) =>
            [...element.attributes].some((attribute) => !attribute.nodeName.startsWith('x-'))
        )) {
            if (isPinned(source)) continue;

            clonedElements.push(this.cloneElement(source));
        }

        // <script>
        for (const source of newDocument.scripts) {
            if (isPinned(source)) continue;

            const cloned = this.viewScript.check(source) ? this.viewScript.queue(source) : this.cloneElement(source);

            cloned.async = source.hasAttribute('async');
            cloned.defer = source.hasAttribute('defer');

            if (cloned.isQueued || (!cloned.src && cloned.innerHTML.length > 0) || (cloned.src && cloned.defer && !cloned.async)) {
                // Inline and deferred scripts
                if (cloned.isQueued) {
                    clonedElements.push(cloned);
                }

                cloned.deferredIndex = deferredScripts.push(cloned) - 1;

                continue;
            }

            clonedElements.push(cloned);
        }

        // Preload blocking resources
        this.timerStart('preload blocking resources');

        const preloadBlockingPromises = [];

        const tagUrlMap = {
            link: 'href',
            script: 'src',
        };

        for (let i = 0; i < clonedElements.length; i++) {
            const cloned = clonedElements[i];
            const tagName = cloned.tagName.toLowerCase();
            const url = cloned[tagUrlMap[tagName]];

            if (!cloned.isQueued && ['link', 'script'].includes(tagName) && (await getCachedContents(url)) === null) {
                let link = document.head.querySelector(`link[href="${url}"][rel="preload"]`);

                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'preload';

                    if (tagName === 'link') {
                        link.href = cloned.href;
                        link.as = 'style';
                    } else if (tagName === 'script') {
                        link.href = cloned.src;
                        link.as = 'script';
                    }

                    for (const property of ['integrity', 'crossOrigin']) {
                        if (cloned[property]) {
                            link[property] = cloned[property];
                        }
                    }

                    preloadBlockingPromises.push(new Promise((resolve) => (link.onload = () => resolve(link.remove()))));

                    document.head.appendChild(link);
                } else {
                    if (link.dataset.loaded) {
                        link.remove();
                    } else {
                        link.onload = null;
                        preloadBlockingPromises.push(new Promise((resolve) => (link.onload = () => resolve(link.remove()))));
                    }
                }
            }

            if (!cloned.isQueued) continue;

            preloadBlockingPromises.push(
                (async () => {
                    let link = document.head.querySelector(`link[href="${url}"][rel="preload"]`);

                    if (link) {
                        if (link.dataset.loaded) {
                            link.remove();
                        } else {
                            link.onload = null;
                            await new Promise((resolve) => (link.onload = () => resolve(link.remove())));
                        }
                    }

                    if (tagName === 'link') {
                        clonedElements[i] = await this.viewStyle.create(cloned);
                    } else if (tagName === 'script') {
                        deferredScripts[cloned.deferredIndex] = await this.viewScript.create(cloned);
                        delete clonedElements[i];
                    }
                })()
            );
        }

        await Promise.allSettled(preloadBlockingPromises);
        this.timerLog('preload blocking resources', 'preload blocking resources in %duration ms');

        // Set current URL
        if (!this.#isPopFromHistory) {
            this.#currentUrl = window.location.href;
        }

        // Emit unload event
        this.emitEvent('unload', {
            current: this.#currentUrl,
            target: targetUrl,
        });

        // Reset window.PageData object
        Object.keys(window.PageData).forEach((key) => delete window.PageData[key]);

        // Reconfigure CSRF token for axios
        this.refreshAxiosCsrfToken(newDocument);

        // Resolve URL hash
        let urlHash = null;

        if (this.#urlHash && this.#urlHash.startsWith(targetUrl)) {
            urlHash = this.#urlHash.split('#')[1];
            targetUrl += '#' + urlHash;

            this.#urlHash = null;
        }

        // Manipulate history
        if (this.#isPopFromHistory) {
            if (targetUrl !== window.location.href) {
                window.history.replaceState(
                    {
                        name: 'bolt',
                        url: targetUrl,
                    },
                    '',
                    targetUrl
                );
            }
        } else {
            this.#scrollPositions[this.#currentUrl] = window.scrollY;
            this.#currentScrollPosition = window.scrollY;

            window.history.scrollRestoration = 'manual';

            window.history.replaceState(
                {
                    name: 'bolt',
                    url: this.#currentUrl,
                },
                '',
                this.#currentUrl
            );

            if (targetUrl !== this.#currentUrl) {
                window.history.pushState(
                    {
                        name: 'bolt',
                        url: targetUrl,
                    },
                    '',
                    targetUrl
                );
            }
        }

        this.#currentUrl = targetUrl;

        const mutateDom = async () => {
            this.timerStart('mutate dom');

            // Emit before-render event
            this.emitEvent('before-render', {
                oldDocument: document,
                newDocument,
            });

            // Replace title and lang
            document.title = newDocument.title;
            document.documentElement.lang = newDocument.documentElement.lang;

            // Sync metadata
            this.timerStart('sync metadata');

            const documentMeta = Array.from(document.head.querySelectorAll('meta'));
            const newDocumentMeta = Array.from(newDocument.head.querySelectorAll('meta'));

            for (const meta of documentMeta) {
                let found = false;

                for (let i = 0; i < newDocumentMeta.length; i++) {
                    if (!meta.isEqualNode(newDocumentMeta[i])) continue;

                    newDocumentMeta[i].remove();
                    newDocumentMeta.splice(i, 1);
                    found = true;

                    break;
                }

                if (!found) {
                    meta.remove();
                }
            }

            if (newDocumentMeta.length > 0) {
                try {
                    document.head.querySelector('meta:last-of-type').after(...newDocumentMeta);
                } catch (error) {
                    document.head.appendChild(...newDocumentMeta);
                }
            }

            this.timerLog('sync metadata', 'sync metadata in %duration ms');

            // Save scroll tops for preserved elements
            const preservedScrollTops = {};

            document.querySelectorAll('[bolt-preserve]').forEach((preserved) => {
                if (!preserved.id) return;

                preservedScrollTops[preserved.id] = preserved.scrollTop;
            });

            // Copy preserved elements
            document.querySelectorAll('[bolt-preserve]').forEach((preserved) => {
                if (!preserved.id) return;

                const found = newDocument.getElementById(preserved.id);

                if (found) {
                    found.replaceWith(preserved);
                } else {
                    delete preservedScrollTops[preserved.id];
                }
            });

            // Replace contents
            let containerReplaced = false;

            for (const selector of this.#targetSelectors) {
                const container = document.querySelector(selector);
                const newContainer = newDocument.querySelector(selector);

                if (container && newContainer) {
                    container.replaceWith(newContainer);
                    containerReplaced = true;

                    break;
                }
            }

            if (containerReplaced) {
                [...document.body.attributes].forEach((attribute) => document.body.removeAttribute(attribute));
                [...newDocument.body.attributes].forEach((attribute) => document.body.setAttribute(attribute.nodeName, attribute.nodeValue));
            } else {
                document.body.replaceWith(newDocument.body);
            }

            // Emit after-render event
            this.emitEvent('after-render');

            // Restore scroll tops for preserved elements
            for (const [id, top] of Object.entries(preservedScrollTops)) {
                document.getElementById(id).scrollTop = top;
            }

            // Scroll to element
            if (urlHash) {
                const element = document.getElementById(urlHash);

                if (element) {
                    window.requestAnimationFrame(() => element.scrollIntoView({ behavior: 'instant' }));
                }
            }

            // Remove transient elements
            document.querySelectorAll('[bolt-transient]').forEach((element) => element.remove());

            this.timerLog('mutate dom', 'mutate DOM in %duration ms');
        };

        // Complete the document preparation
        const finish = async () => {
            // Insert cloned elements
            this.timerStart('blocking resources');

            const loadBlockingPromises = [];

            for (const cloned of clonedElements) {
                if (!cloned) continue;

                const tagName = cloned.tagName.toLowerCase();
                let target;

                if (tagName === 'link' || tagName === 'style') {
                    target = 'head';
                } else if (tagName === 'template' || tagName === 'script') {
                    target = 'body';
                }

                if (!cloned.hasAttribute('bolt-pin')) {
                    cloned.setAttribute('bolt-transient', '');
                }

                loadBlockingPromises.push(
                    new Promise((resolve) => {
                        if ((tagName === 'link' || tagName === 'script') && cloned[tagUrlMap[tagName]]) {
                            cloned.onload = () => {
                                resolve();
                                cloned.onload = null;
                            };
                        } else {
                            resolve();
                        }
                    })
                );

                const idPinned = idPinnedElements[cloned.getAttribute('bolt-pin')];

                if (idPinned) {
                    idPinned.replaceWith(cloned);
                } else {
                    document[target].appendChild(cloned);
                }
            }

            await Promise.allSettled(loadBlockingPromises);
            this.timerLog('blocking resources', 'load blocking resources in %duration ms');

            // Insert deferred scripts
            this.timerStart('deferred resources');

            for (const deferred of deferredScripts) {
                if (!deferred.hasAttribute('bolt-pin')) {
                    deferred.setAttribute('bolt-transient', '');
                }

                const idPinned = idPinnedElements[deferred.getAttribute('bolt-pin')];

                if (idPinned) {
                    idPinned.replaceWith(deferred);
                } else {
                    document.body.appendChild(deferred);
                }
            }

            this.timerLog('deferred resources', 'load deferred resources in %duration ms');

            // Emit load event
            this.emitEvent('load');

            // Restart Alpine mutation observer
            this.timerStart('restart alpine');

            Alpine.flushAndStopDeferringMutations();
            Alpine.startObservingMutations();

            this.timerLog('restart alpine', 'restart Alpine in %duration ms');

            // Scroll and autofocus
            window.requestAnimationFrame(() => {
                if (urlHash) return;

                const behavior = 'instant';

                if (this.#isPopFromHistory) {
                    window.scrollTo({ top: this.#scrollPositions[targetUrl] ?? 0, behavior });
                } else {
                    if (this.#preserveCurrentScroll) {
                        window.scrollTo({ top: this.#currentScrollPosition, behavior });
                        this.#preserveCurrentScroll = false;
                    } else {
                        const autofocusElement = document.querySelector('[autofocus]');

                        if (autofocusElement) {
                            autofocusElement.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
                            window.requestAnimationFrame(() => autofocusElement.focus());
                        } else {
                            window.scrollTo({ top: 0, behavior });
                        }
                    }
                }

                this.#isPopFromHistory = false;
            });
        };

        this.#counter++;

        // View transition
        if (transitionEnabled && !document.hidden && document.startViewTransition) {
            this.timerStart('view transition');

            const transition = document.startViewTransition(async () => {
                await mutateDom(targetUrl, newDocument);
                await finish();
            });

            transition.finished.then(() => {
                this.timerLog('view transition', 'view transition in %duration ms');
                this.timerLog('render view', 'render view in %duration ms');
            });
        } else {
            await mutateDom(targetUrl, newDocument);
            await finish();
            this.timerLog('render view', 'render view in %duration ms');
        }
    }

    static getMetadata(targetDocument, name, defaultValue) {
        const metadata = targetDocument.head.querySelector(`meta[name="${name}"]`);

        return metadata ? metadata.getAttribute('content') : defaultValue;
    }

    static setMetadata(targetDocument, name, value) {
        let metadata = targetDocument.head.querySelector(`meta[name="${name}"]`);

        if (!metadata) {
            metadata = `<meta name="${name}">`;
            metadata = document.createRange().createContextualFragment(metadata).firstElementChild;
            targetDocument.head.appendChild(metadata);
        }

        metadata.setAttribute('content', value);
    }

    static isElementDisabled(element) {
        const disable = element.closest('[bolt-disable]');
        const enable = element.closest('[bolt-enable]');

        if ((disable && !enable) || (disable && enable && !disable.contains(enable))) return true;

        return false;
    }

    static cloneElement(source) {
        return document.createRange().createContextualFragment(source.outerHTML).firstElementChild;
    }

    static timerStart(name) {
        this.#timers[name] = performance.now();
    }

    static timerLog(name, template = '%name run for %duration ms') {
        if (!this.#showTimingLogs) return;

        if (!this.#timers[name]) {
            console.warn(`Timer '${name}' doesn't exist.`);
            return;
        }

        console.info(
            `%cBolt: %c${template.replace('%name', name).replace('%duration', `%c${(performance.now() - this.#timers[name]).toFixed(2)}`)}`,
            'color: orange',
            '',
            'color: tomato'
        );
    }

    static setTargetSelectors(...selectors) {
        this.#targetSelectors = selectors;
    }

    static skipPopStateHandling(callback) {
        this.#skipPopStateHandling = callback;
    }

    static getElements() {
        return {
            links: [...document.head.querySelectorAll('link[rel="stylesheet"]')],
            styles: [...document.head.querySelectorAll('style')],
            templates: [...document.body.querySelectorAll('template[id]')].filter((element) =>
                [...element.attributes].some((attribute) => !attribute.nodeName.startsWith('x-'))
            ),
            scripts: [...document.scripts],
        };
    }

    static emitEvent(type, detail = {}) {
        this.timerStart(`event ${type}`);

        for (const item of this.#eventListeners.values()) {
            if (item.type === type) {
                item.listener({
                    counter: this.#counter,
                    detail,
                    type,
                    timestamp: Date.now(),
                });

                if (item.options.once) {
                    this.#eventListeners.delete(item.listener);
                }
            }
        }

        this.timerLog(`event ${type}`, `execute '${type}' event listeners in %duration ms`);
    }

    static on(type, listener, options = {}) {
        options = Object.assign({ once: false }, options);

        if (!this.#eventListeners.has(listener)) {
            this.#eventListeners.set(listener, { type, listener, options });
        }
    }

    static once(type, listener) {
        this.on(type, listener, { once: true });
    }

    static off(type, listener) {
        const eventListener = this.#eventListeners.get(listener);

        if (eventListener && eventListener.type === type) {
            this.#eventListeners.delete(listener);
        }
    }

    static startProgressBar() {
        const [progressContainer, progressBar] = this.getProgressBarElements();

        progressContainer.style.transition = 'none';
        progressContainer.style.opacity = 100;

        progressBar.style.transition = 'none';
        progressBar.style.width = '0';
        progressBar.offsetWidth;
        progressBar.style.transition = 'width 30s cubic-bezier(0,1.1,0.4,1) 0s';
        progressBar.style.width = '100%';
    }

    static stopProgressBar() {
        const [progressContainer, progressBar] = this.getProgressBarElements();

        progressContainer.style.transition = 'opacity 1s ease-out';
        progressContainer.style.opacity = 0;

        const clientWidth = document.documentElement.clientWidth;
        const currentWidth = Math.ceil(window.getComputedStyle(progressBar).width.slice(0, -2) + clientWidth / 100);
        const balanceWidth = Math.min(clientWidth / 2, clientWidth - currentWidth);

        progressBar.style.transition = 'none';
        progressBar.style.width = currentWidth + 'px';
        progressBar.offsetWidth;
        progressBar.style.transition = `width ${Math.round((balanceWidth / (clientWidth / 2)) * 250)}ms ease-out`;
        progressBar.style.width = '100%';
    }

    static getProgressBarElements() {
        let progressContainer = document.getElementById('bolt-progress');

        if (!progressContainer) {
            progressContainer = document.createRange().createContextualFragment(`<div id="bolt-progress" class="progress position-fixed rounded-0" style="background: transparent; opacity: 0; top: 0; left: 0; right: 0; z-index: 10000"><div class="progress-bar progress-bar-striped progress-bar-animated rounded-0" style="width: 0; height: 3px"></div></div>`).firstElementChild;

            document.body.prepend(progressContainer);
        }

        return [progressContainer, progressContainer.firstElementChild];
    }
}
